import React, { useState, useEffect } from "react";
import { Card, Col, Row, Nav, Tab, Alert, Badge, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUser, 
  faChartLine, 
  faBriefcase, 
  faShieldAlt, 
  faRocket,
  faCheckCircle,
  faExclamationTriangle,
  faCog,
  faPhone
} from "@fortawesome/free-solid-svg-icons";
import Settings from "../social/Settings";
import kairosLogo from "assets/img/logos/KairosIQ_logo.png";
import { useAgentSummary } from "hooks/useAgentSalesAnalytics";
import AgentSetupWizard from "components/modules/analytics/AgentSetupWizard";
import ProfessionalSettings from "components/modules/analytics/ProfessionalSettings";
import SecuritySettings from "components/modules/security/SecuritySettings";
import PhoneNumberManagement from "components/modules/communications/PhoneNumberManagement";
import { useAtom } from "jotai";
import { user_info } from "jotai/UserJotaiInstance";
import { UserInfo } from "interfaces/user";

const ProfileSettings = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const { summary } = useAgentSummary();
  const [setupComplete, setSetupComplete] = useState(false);
  const [professionalComplete, setProfessionalComplete] = useState(false);
  const [communicationsComplete, setCommunicationsComplete] = useState(false);
  const [setupWizardKey, setSetupWizardKey] = useState(0); // Force remount of wizard
  const [showReconfigureModal, setShowReconfigureModal] = useState(false);
  const [userInfo] = useAtom<UserInfo>(user_info);

  // Check if agent analytics is set up (using onboarding status instead of analytics summary)
  useEffect(() => {
    const checkAgentSetup = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const response = await fetch('/api/onboarding/status', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.agent_analytics?.setup_complete && result.data?.agentInfo?.mlsAgentId) {
            setSetupComplete(true);
            // Persist the completion state in localStorage like Professional tab
            localStorage.setItem('agentAnalyticsComplete', 'true');
            console.log('✅ Agent analytics setup detected via onboarding status');
          } else {
            console.log('❌ Agent analytics setup not complete according to onboarding status');
          }
        }
      } catch (error) {
        console.warn('Failed to check agent setup status:', error);
      }
    };

    checkAgentSetup();
  }, []); // Check once on mount

  // Also check with the summary data (fallback)
  useEffect(() => {
    if (summary?.agentInfo?.mls_agent_id) {
      setSetupComplete(true);
      // Persist the completion state in localStorage like Professional tab
      localStorage.setItem('agentAnalyticsComplete', 'true');
      console.log('✅ Agent analytics setup detected via summary data');
    }
  }, [summary]);

  // Check for previously saved agent analytics completion state on mount
  useEffect(() => {
    const savedAgentAnalytics = localStorage.getItem('agentAnalyticsComplete');
    if (savedAgentAnalytics === 'true') {
      setSetupComplete(true);
    }
  }, []); // Run once on mount

  // Check for previously saved professional completion state on mount
  useEffect(() => {
    const savedBusinessData = localStorage.getItem('businessProfileComplete');
    if (savedBusinessData === 'true') {
      setProfessionalComplete(true);
    }
  }, []); // Run once on mount

  // Check for communications setup completion
  useEffect(() => {
    const checkCommunicationsSetup = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const response = await fetch('/api/platform-messaging/my-phone-number', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.phone_number) {
            setCommunicationsComplete(true);
            localStorage.setItem('communicationsComplete', 'true');
          } else {
            setCommunicationsComplete(false);
            localStorage.removeItem('communicationsComplete');
          }
        }
      } catch (error) {
        console.warn('Failed to check communications setup:', error);
      }
    };

    checkCommunicationsSetup();
  }, []);

  // Check for previously saved communications completion state on mount
  useEffect(() => {
    const savedCommunications = localStorage.getItem('communicationsComplete');
    if (savedCommunications === 'true') {
      setCommunicationsComplete(true);
    }
  }, []); // Run once on mount

  // Listen for professional completion (could be enhanced with proper state management)
  useEffect(() => {
    const checkProfessionalCompletion = () => {
      // This is a simple check - in a real app you'd want proper state management
      const savedBusinessData = localStorage.getItem('businessProfileComplete');
      if (savedBusinessData === 'true') {
        setProfessionalComplete(true);
      }
    };
    
    const checkAgentAnalyticsCompletion = async () => {
      // First check localStorage
      const savedAgentAnalytics = localStorage.getItem('agentAnalyticsComplete');
      if (savedAgentAnalytics === 'true') {
        setSetupComplete(true);
        return;
      }

      // If not in localStorage, check onboarding status
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const response = await fetch('/api/onboarding/status', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.agent_analytics?.setup_complete && result.data?.agentInfo?.mlsAgentId) {
            setSetupComplete(true);
            localStorage.setItem('agentAnalyticsComplete', 'true');
          }
        }
      } catch (error) {
        // Silently fail, don't spam console
      }
    };

    const checkCommunicationsCompletion = async () => {
      // First check localStorage
      const savedCommunications = localStorage.getItem('communicationsComplete');
      if (savedCommunications === 'true') {
        setCommunicationsComplete(true);
        return;
      }

      // If not in localStorage, check API
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const response = await fetch('/api/platform-messaging/my-phone-number', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.phone_number) {
            setCommunicationsComplete(true);
            localStorage.setItem('communicationsComplete', 'true');
          }
        }
      } catch (error) {
        // Silently fail, don't spam console
      }
    };
    
    checkProfessionalCompletion();
    checkAgentAnalyticsCompletion();
    checkCommunicationsCompletion();
    // Check periodically or listen for storage events
    const interval = setInterval(() => {
      checkProfessionalCompletion();
      checkAgentAnalyticsCompletion();
      checkCommunicationsCompletion();
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Add listener for storage events to sync state across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'businessProfileComplete' && e.newValue === 'true') {
        setProfessionalComplete(true);
      }
      if (e.key === 'agentAnalyticsComplete' && e.newValue === 'true') {
        setSetupComplete(true);
      }
      if (e.key === 'communicationsComplete' && e.newValue === 'true') {
        setCommunicationsComplete(true);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle agent analytics reconfiguration
  const handleReconfigure = () => {
    // Reset setup state to show the setup wizard
    setSetupComplete(false);
    
    // Force complete remount of setup wizard to reset all internal state
    // This ensures the wizard starts fresh from step 1
    setSetupWizardKey(prev => prev + 1);
    
    // Clear all cached setup state
    localStorage.removeItem('agentSetupComplete');
    localStorage.removeItem('agentProfileData');
    localStorage.removeItem('agentVerificationData');
    localStorage.removeItem('agentSummaryCache');
    localStorage.removeItem('agentAnalyticsComplete');
    
    // Close modal first, then the component will re-render to show setup wizard
    setShowReconfigureModal(false);
  };

  const tabConfig = [
    {
      key: 'personal',
      title: 'Personal Profile',
      icon: faUser,
      badge: null,
      description: 'Basic information and professional details'
    },
    {
      key: 'agent-analytics',
      title: 'Agent Analytics',
      icon: faChartLine,
      badge: setupComplete ? 'success' : 'warning',
      description: setupComplete ? 'Performance tracking active' : 'Setup required'
    },
    {
      key: 'professional',
      title: 'Professional',
      icon: faBriefcase,
      badge: professionalComplete ? 'success' : null,
      description: professionalComplete ? 'Business profile complete' : 'Business settings and integrations'
    },
    {
      key: 'security',
      title: 'Security',
      icon: faShieldAlt,
      badge: null,
      description: 'Privacy and security preferences'
    },
    {
      key: 'communications',
      title: 'Communications',
      icon: faPhone,
      badge: communicationsComplete ? 'success' : null,
      description: 'Phone number management'
    }
  ];

  const StatusIndicator = () => {
    const completionItems = [
      { label: 'Profile Setup', completed: true },
      { label: 'Agent Analytics', completed: setupComplete },
      { label: 'Professional Settings', completed: professionalComplete },
      { label: 'Communications', completed: communicationsComplete },
      { label: 'MLS Integration', completed: setupComplete },
      { label: 'Performance Tracking', completed: setupComplete }
    ];

    const completedCount = completionItems.filter(item => item.completed).length;
    const completionPercentage = (completedCount / completionItems.length) * 100;

    return (
      <div 
        className="status-card p-4 mb-1"
        style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '16px'
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>
            <FontAwesomeIcon icon={faRocket} className="me-2 text-success" />
            Setup Progress
          </h6>
          <Badge 
            bg={completionPercentage === 100 ? 'success' : 'warning'}
            className="px-3 py-2"
          >
            {Math.round(completionPercentage)}% Complete
          </Badge>
        </div>
        
        <div className="progress mb-3" style={{ height: '8px', borderRadius: '4px' }}>
          <div 
            className="progress-bar bg-success" 
            style={{ 
              width: `${completionPercentage}%`,
              borderRadius: '4px',
              transition: 'width 0.6s ease'
            }}
          />
        </div>

        <Row className="g-2">
          {completionItems.map((item, index) => (
            <Col key={index} xs={6}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon 
                  icon={item.completed ? faCheckCircle : faExclamationTriangle}
                  className={`me-2 ${item.completed ? 'text-success' : 'text-warning'}`}
                  size="sm"
                />
                <small className={item.completed ? 'text-success' : 'text-muted'}>
                  {item.label}
                </small>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const TabContent = () => {
    switch (activeTab) {
      case 'personal':
        return <Settings />;
      case 'agent-analytics':
        return setupComplete ? (
          <div>
            {/* Success Alert with Agent Info */}
            <Alert variant="success" className="d-flex align-items-center mb-4">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              <div>
                <strong>Agent Analytics Active!</strong>
                <div className="small mt-1">
                  {summary?.agentInfo?.agent_full_name ? (
                    <>MLS Agent: {summary.agentInfo.agent_full_name} ({summary.agentInfo.mls_agent_id})</>
                  ) : (
                    <>Your MLS profile is connected and analytics are tracking your performance data</>
                  )}
                </div>
              </div>
            </Alert>

            {/* Enhanced Action Buttons with Workflow Progression */}
            <div className="d-flex flex-wrap gap-3 align-items-center mb-4">
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => setActiveTab('professional')}
                className="px-4"
              >
                <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                Continue to Professional Settings
              </Button>
              
              <Button 
                variant="outline-primary" 
                onClick={() => window.open('/apps/crm/agent-analytics', '_blank')}
              >
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                View Full Dashboard
              </Button>
              
              <Button 
                variant="outline-secondary"
                onClick={() => setShowReconfigureModal(true)}
              >
                <FontAwesomeIcon icon={faCog} className="me-2" />
                Reconfigure Setup
              </Button>
            </div>

            {/* Workflow Progress Indicator */}
            <Alert variant="info" className="d-flex align-items-center mb-4">
              <FontAwesomeIcon icon={faRocket} className="me-2" />
              <div className="flex-grow-1">
                <div className="d-flex align-items-center mb-2">
                  <strong>Setup Progress:</strong>
                  <div className="ms-3 d-flex align-items-center">
                    <Badge bg="success" className="me-2">✓ Personal</Badge>
                    <Badge bg="success" className="me-2">✓ Analytics</Badge>
                    <Badge bg={professionalComplete ? 'success' : 'warning'} className="me-2">
                      {professionalComplete ? '✓' : '○'} Professional
                    </Badge>
                    <Badge bg={communicationsComplete ? 'success' : 'secondary'} className="me-2">
                      {communicationsComplete ? '✓' : '○'} Communications
                    </Badge>
                    <Badge bg="secondary">○ Security</Badge>
                  </div>
                </div>
                <strong>Next Step:</strong> Complete your professional profile to maximize lead generation and networking opportunities.
                <div className="small mt-1">
                  Setup your business information, professional contacts, and integrations.
                </div>
              </div>
            </Alert>

            {/* Embedded Analytics Dashboard Preview */}
            <div className="agent-analytics-preview">
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-gradient-primary text-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">
                      <FontAwesomeIcon icon={faChartLine} className="me-2" />
                      Analytics Overview
                    </h5>
                    <Button 
                      variant="light" 
                      size="sm"
                      onClick={() => window.open('/apps/crm/agent-analytics', '_blank')}
                    >
                      View Full Dashboard
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {summary?.agentInfo ? (
                    <Row className="gy-4">
                      <Col md={6}>
                        <div className="text-center p-3 border rounded bg-light">
                          <h3 className="text-primary mb-1">
                            <FontAwesomeIcon icon={faChartLine} />
                          </h3>
                          <h6 className="mb-0">Analytics Ready</h6>
                          <small className="text-muted">MLS data integration active</small>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-center p-3 border rounded bg-light">
                          <h3 className="text-success mb-1">
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </h3>
                          <h6 className="mb-0">Profile Verified</h6>
                          <small className="text-muted">{summary.agentInfo.verification_status}</small>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <Alert variant="info" className="mb-0">
                          <FontAwesomeIcon icon={faRocket} className="me-2" />
                          <strong>Ready for Analytics!</strong> Your agent profile is configured and ready to track performance metrics.
                          Click "View Full Dashboard" to see detailed analytics and insights.
                        </Alert>
                      </Col>
                    </Row>
                  ) : (
                    <div className="text-center py-4">
                      <FontAwesomeIcon icon={faChartLine} size="2x" className="text-muted mb-3" />
                      <h6>Analytics Dashboard</h6>
                      <p className="text-muted mb-0">
                        Performance metrics and insights will appear here once your MLS integration is active.
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>
        ) : (
          <AgentSetupWizard 
            key={setupWizardKey} // Force remount when key changes
            onComplete={() => setSetupComplete(true)} 
            onCancel={() => setSetupComplete(true)} // Return to analytics view
            userId={userInfo?.id?.toString()}
          />
        );
      case 'professional':
        return <ProfessionalSettings />;
      case 'security':
        return <SecuritySettings userId={userInfo?.id?.toString()} />;
      case 'communications':
        return <PhoneNumberManagement />;
      default:
        return <Settings />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', marginTop: '-2rem' }}>
      <Row className="h-100 g-0">
        <Col xs={12} xl={12} xxl={11} className="mx-auto" style={{ marginTop: 0 }}>
          {/* Enhanced AI Header */}
          <div 
            className="px-4"
            style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(15px)',
              margin: '0',
              position: 'relative',
              overflow: 'hidden',
              padding: '1.5rem',
              marginBottom: '2rem',
              borderRadius: '0 0 16px 16px'
            }}
          >
            {/* Neural Network Background Effect */}
            <div 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d4ff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                opacity: 0.3
              }}
            />
            
            <Row className="align-items-center">
              <Col xl={8}>
                <div className="d-flex align-items-center header-content-flex-wrapper">
                  <div 
                    className="d-flex align-items-center justify-content-center me-4 logo-container-responsive"
                    style={{ 
                      width: "56px", 
                      height: "56px"
                    }}
                  >
                    <img 
                      src={kairosLogo} 
                      alt="KairosIQ Logo" 
                      style={{ 
                        width: "100%", 
                        height: "100%", 
                        objectFit: "contain",
                        filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.3))'
                      }} 
                    />
                  </div>
                  
                  <div className="header-text-responsive">
                    <h2 className="mb-2 fw-bold" style={{ color: '#1f2937', fontSize: '1.75rem' }}>
                      Profile Settings
                    </h2>
                    <p className="mb-0" style={{ color: '#6b7280', fontSize: '1rem' }}>
                      Manage your personal information, agent analytics, and professional settings
                    </p>
                  </div>
                </div>
              </Col>
              
              <Col xl={4} className="text-end">
                <StatusIndicator />
              </Col>
            </Row>
          </div>

          {/* Enhanced Main Settings with Tabbed Interface */}
          <Card 
            className="border-0 shadow-lg"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 212, 255, 0.1) !important',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 212, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.1) inset !important',
              overflow: 'hidden'
            }}
          >
            <Card.Header 
              className="border-bottom-0 p-0"
              // style={{
              //   background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
              // }}
            >
              <Tab.Container 
                activeKey={activeTab} 
                onSelect={(key) => setActiveTab(key || 'personal')}
              >
                <Nav variant="tabs" className="nav-tabs-enhanced">
                  {tabConfig.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link 
                        eventKey={tab.key}
                        className="d-flex align-items-center py-3 px-4"
                        style={{ borderRadius: '0' }}
                      >
                        <FontAwesomeIcon 
                          icon={tab.icon} 
                          className="me-2" 
                        />
                        <div className="text-start">
                          <div className="d-flex align-items-center">
                            <span className="fw-semibold">{tab.title}</span>
                            {tab.badge && (
                              <Badge 
                                bg={tab.badge} 
                                className="ms-2"
                                style={{ fontSize: '10px' }}
                              >
                                {tab.badge === 'success' ? '✓' : '!'}
                              </Badge>
                            )}
                          </div>
                          <small className="text-muted d-none d-md-block">
                            {tab.description}
                          </small>
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Tab.Container>
            </Card.Header>
            
            <Card.Body className="p-5">
              <TabContent />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enhanced AI Animation Styles */}
      <style>
        {`
          .header-content-flex-wrapper {
            flex-wrap: wrap;
            gap: 1rem;
          }
          
          .logo-container-responsive {
            flex-shrink: 0;
          }
          
          .header-text-responsive {
            min-width: 0;
            flex: 1;
          }
          
          @media (max-width: 768px) {
            .header-content-flex-wrapper {
              flex-direction: column;
              text-align: center;
            }
            
            .logo-container-responsive {
              align-self: center;
            }
            
            .header-text-responsive h2 {
              font-size: 1.5rem !important;
            }
            
            .header-text-responsive p {
              font-size: 0.9rem !important;
            }
          }
          
          .nav-tabs-enhanced .nav-link {
            border: none;
            background: transparent;
            transition: all 0.3s ease;
            border-radius: 12px 12px 0 0;
            margin-right: 4px;
          }
          
          .nav-tabs-enhanced .nav-link:hover {
            background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%);
            transform: translateY(-2px);
          }
          
          .nav-tabs-enhanced .nav-link.active {
            background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(102, 126, 234, 0.15) 100%);
            border-bottom: 3px solid #00d4ff;
            font-weight: 600;
          }
          
          .status-card {
            backdrop-filter: blur(10px);
            transition: all 0.3s ease;
          }
          
          .status-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
          }
        `}
      </style>

      {/* Reconfigure Confirmation Modal */}
      <Modal 
        show={showReconfigureModal} 
        onHide={() => setShowReconfigureModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-bottom-0">
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCog} className="me-2 text-warning" />
            Reconfigure Agent Analytics
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <Alert variant="warning" className="d-flex align-items-start">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 mt-1" />
            <div>
              <strong>This will restart the agent setup wizard</strong>
              <div className="mt-2">
                • You'll return to Step 1 of the setup process<br/>
                • Current analytics configuration will be cleared<br/>
                • You can enter new MLS agent information<br/>
                • Any cached verification data will be removed
              </div>
            </div>
          </Alert>
          
          <p className="mb-0 text-muted">
            This is useful if you need to change your MLS agent ID, fix setup issues, or start fresh. 
            Your historical analytics data will not be affected.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowReconfigureModal(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleReconfigure}
            className="px-4"
          >
            <FontAwesomeIcon icon={faCog} className="me-2" />
            Yes, Reconfigure
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProfileSettings;
